import React, { useContext, useEffect, useMemo, useState } from "react";
import { Drawer, Table, Input, Select, Button, Space, Tag, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import trans, { isRTL } from "../../../utils";
import { popupcontext } from "../../../context/Popupscontext";
import { useGetAllPOIsQuery } from "../../../Redux/service/POIS/POIs";
import { useGetAllTechnicianQuery } from "../../../Redux/service/Technician/Technician";
import {
  useGetUsersPOIAssignmentsQuery,
  useBulkUpdatePOIAssignmentsMutation,
} from "../../../Redux/service/Permissions/POIAssignments";

// Pane accent colors — keep the two sections visually distinct
const AVAILABLE_ACCENT = {
  bg: "#fafafa",
  border: "#d9d9d9",
  text: "rgba(0, 0, 0, 0.88)",
};
const ASSIGNED_ACCENT = {
  bg: "#f6ffed",
  border: "#b7eb8f",
  text: "#389e0d",
};

// Fill the drawer height: viewport minus header + pane card header + toolbar
// rows + table header + footer chrome (multi mode has an extra staff-selector
// row). antd virtual tables need scroll.y as a number.
const usePaneHeight = (offset) => {
  const calc = () => Math.max(300, window.innerHeight - offset);
  const [height, setHeight] = useState(calc);
  useEffect(() => {
    const onResize = () => setHeight(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);
  return height;
};

/**
 * POI assignment drawer with two modes:
 *
 * 1. Single mode (userId prop provided) — opened from a field staff row or the
 *    users connections popup. Assigns POIs to THAT person only.
 *
 * 2. Bulk mode (no userId) — opened from the page-level "Bulk Assign POIs"
 *    button; pick any number of staff in the drawer.
 *
 * Transfer-list model: the ASSIGNED pane holds POIs assigned to ALL selected
 * staff; POIs assigned to only SOME staff stay in AVAILABLE with an orange
 * "k/n" badge (tooltip lists who has them) so the remaining staff can still
 * receive them — assigning skips duplicates server-side. The two arrow
 * buttons between the panes move the selected rows across. All changes are
 * staged; Save sends ONE request: { userIds, add, remove }.
 */
export default function AssignPOIsDrawer({ userId, userName, open, onClose, onSaved }) {
  const { setReloadData } = useContext(popupcontext);
  const fixedUser = !!userId;
  const paneHeight = usePaneHeight(fixedUser ? 390 : 440);

  // ---- data ----------------------------------------------------------------
  const { data: allPoisResp, isFetching: loadingPois } = useGetAllPOIsQuery(
    { page: 1, limit: 10000 },
    { skip: !open, refetchOnMountOrArgChange: true }
  );
  // Staff picker options are only needed in bulk mode
  const { data: techniciansResp } = useGetAllTechnicianQuery(
    { page: 1, pageSize: 10000 },
    { skip: !open || fixedUser }
  );

  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const { data: assignmentsResp, isFetching: loadingAssignments } =
    useGetUsersPOIAssignmentsQuery(
      { userIds: selectedStaffIds },
      { skip: !open || selectedStaffIds.length === 0 }
    );
  const [bulkUpdate, { isLoading: saving }] = useBulkUpdatePOIAssignmentsMutation();

  // ---- staged state ----------------------------------------------------------
  const [toAdd, setToAdd] = useState(new Set());
  const [toRemove, setToRemove] = useState(new Set());
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);

  // Reset everything whenever the drawer opens
  useEffect(() => {
    if (open) {
      setSelectedStaffIds(userId ? [userId] : []);
      setToAdd(new Set());
      setToRemove(new Set());
      setLeftSelected([]);
      setRightSelected([]);
      setLeftSearch("");
      setRightSearch("");
      setTypeFilter(null);
      setCityFilter(null);
    }
  }, [open, userId]);

  const staffCount = selectedStaffIds.length;

  // ---- staff options (bulk mode) ------------------------------------------------
  const staffOptions = useMemo(
    () =>
      (techniciansResp?.technicians || [])
        .filter((t) => t.tc_user_id)
        .map((t) => ({ value: t.tc_user_id, label: t.name })),
    [techniciansResp]
  );

  const staffNameById = useMemo(() => {
    const map = new Map(staffOptions.map((o) => [o.value, o.label]));
    (assignmentsResp?.data?.users || []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [staffOptions, assignmentsResp]);

  // ---- data shaping ------------------------------------------------------------
  const allPois = useMemo(
    () => (Array.isArray(allPoisResp?.data) ? allPoisResp.data : []),
    [allPoisResp]
  );

  // poiId -> Set of selected staff ids currently assigned to it (server state)
  const assignedMap = useMemo(() => {
    const map = new Map();
    (assignmentsResp?.data?.assignments || []).forEach(({ userId: uid, poiIds }) => {
      if (!selectedStaffIds.includes(uid)) return;
      poiIds.forEach((poiId) => {
        if (!map.has(poiId)) map.set(poiId, new Set());
        map.get(poiId).add(uid);
      });
    });
    return map;
  }, [assignmentsResp, selectedStaffIds]);

  // id -> poi lookup; assignment-embedded POIs cover ids missing from the list
  const poiById = useMemo(() => {
    const map = new Map();
    (assignmentsResp?.data?.pois || []).forEach((p) => map.set(p.id, p));
    allPois.forEach((p) => map.set(p.id, p));
    return map;
  }, [allPois, assignmentsResp]);

  const typeOptions = useMemo(
    () =>
      [...new Set(allPois.map((p) => p.type).filter(Boolean))]
        .sort()
        .map((t) => ({ value: t, label: t })),
    [allPois]
  );
  const cityOptions = useMemo(
    () =>
      [...new Set(allPois.map((p) => p.city).filter(Boolean))]
        .sort()
        .map((c) => ({ value: c, label: c })),
    [allPois]
  );

  const matchesText = (p, q) =>
    !q ||
    `${p.name || ""} ${p.code || ""} ${p.address || ""} ${p.city || ""}`
      .toLowerCase()
      .includes(q);

  const matchesFilters = (p, q) =>
    (!typeFilter || p.type === typeFilter) &&
    (!cityFilter || p.city === cityFilter) &&
    matchesText(p, q);

  // ASSIGNED pane = staged additions + POIs every selected staff already has
  // (unless staged for removal). Partial POIs stay in AVAILABLE so the staff
  // missing them can still be assigned.
  const holderCount = (id) => assignedMap.get(id)?.size || 0;
  const isOnRight = (id) =>
    toAdd.has(id) ||
    (staffCount > 0 && holderCount(id) >= staffCount && !toRemove.has(id));

  const availableRows = useMemo(() => {
    const q = leftSearch.trim().toLowerCase();
    const rows = allPois.filter(
      (p) => p.status === true && !isOnRight(p.id) && matchesFilters(p, q)
    );
    // Partially-assigned POIs missing from the main list (e.g. inactive)
    // must still be visible somewhere — surface them here
    const listedIds = new Set(allPois.map((p) => p.id));
    [...assignedMap.keys()].forEach((id) => {
      if (listedIds.has(id) || isOnRight(id)) return;
      const p = poiById.get(id);
      if (p && matchesFilters(p, q)) rows.push(p);
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPois, assignedMap, toAdd, toRemove, poiById, leftSearch, typeFilter, cityFilter, staffCount]);

  const assignedRows = useMemo(() => {
    const q = rightSearch.trim().toLowerCase();
    const ids = new Set([...assignedMap.keys(), ...toAdd]);
    return [...ids]
      .filter((id) => isOnRight(id))
      .map((id) => poiById.get(id))
      .filter(Boolean)
      .filter((p) => matchesText(p, q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedMap, toAdd, toRemove, poiById, rightSearch, staffCount]);

  const hasChanges = staffCount > 0 && (toAdd.size > 0 || toRemove.size > 0);

  // ---- actions -------------------------------------------------------------------
  const assignIds = (ids) => {
    setToRemove((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setToAdd((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        // Only stage what isn't already assigned to ALL selected staff
        if (holderCount(id) < staffCount) next.add(id);
      });
      return next;
    });
    setLeftSelected([]);
    setRightSelected([]);
  };

  const removeIds = (ids) => {
    setToAdd((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setToRemove((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (holderCount(id) > 0) next.add(id);
      });
      return next;
    });
    setLeftSelected([]);
    setRightSelected([]);
  };

  const handleStaffChange = (ids) => {
    setSelectedStaffIds(ids);
    setLeftSelected([]);
    setRightSelected([]);
  };

  const handleSave = async () => {
    try {
      const res = await bulkUpdate({
        userIds: selectedStaffIds,
        add: [...toAdd],
        remove: [...toRemove],
      }).unwrap();
      toast.success(
        `${trans("POI assignments updated successfully")} (+${
          res?.data?.added ?? 0
        } / -${res?.data?.removed ?? 0})`,
        { autoClose: 1500, rtl: localStorage.getItem("lang") === "ar" }
      );
      setReloadData && setReloadData(true);
      onSaved && onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(
        trans(error?.data?.message) || trans("Failed to update POI assignments"),
        { autoClose: 2000, rtl: localStorage.getItem("lang") === "ar" }
      );
    }
  };

  const handleCancel = () => {
    setToAdd(new Set());
    setToRemove(new Set());
    onClose();
  };

  // ---- table config ------------------------------------------------------------------
  const baseColumns = [
    {
      title: trans("Name"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: trans("Type"),
      dataIndex: "type",
      key: "type",
      width: 105,
      ellipsis: true,
    },
    {
      title: trans("City"),
      dataIndex: "city",
      key: "city",
      width: 105,
      ellipsis: true,
    },
  ];

  // AVAILABLE pane status (bulk mode): staged removals + partial membership
  const leftStatusColumn = {
    title: trans("Status"),
    key: "assignStatus",
    width: 105,
    render: (_, record) => {
      if (toRemove.has(record.id)) {
        return <Tag color="red">{trans("Will remove")}</Tag>;
      }
      const holders = assignedMap.get(record.id);
      if (!holders || holders.size === 0) return null;
      const names = [...holders]
        .map((uid) => staffNameById.get(uid) || `#${uid}`)
        .join(", ");
      return (
        <Tooltip title={`${trans("Assigned to")}: ${names}`}>
          <Tag color="orange">{`${holders.size}/${staffCount}`}</Tag>
        </Tooltip>
      );
    },
  };

  // ASSIGNED pane status (bulk mode): staged vs already assigned to all
  const rightStatusColumn = {
    title: trans("Status"),
    key: "assignStatus",
    width: 105,
    render: (_, record) =>
      toAdd.has(record.id) ? (
        <Tag color="green">{trans("New")}</Tag>
      ) : (
        <Tag color="blue">{trans("All")}</Tag>
      ),
  };

  const leftColumns = fixedUser ? baseColumns : [...baseColumns, leftStatusColumn];
  const rightColumns = fixedUser ? baseColumns : [...baseColumns, rightStatusColumn];

  const paneTable = (rows, columns, selectedKeys, setSelectedKeys, loading, emptyText) => (
    <Table
      size="small"
      rowKey="id"
      virtual
      columns={columns}
      dataSource={rows}
      loading={loading}
      locale={{ emptyText }}
      pagination={false}
      scroll={{ y: paneHeight }}
      rowSelection={{
        selectedRowKeys: selectedKeys,
        onChange: setSelectedKeys,
        columnWidth: 40,
      }}
      onRow={(record) => ({
        onClick: () => {
          setSelectedKeys((prev) =>
            prev.includes(record.id)
              ? prev.filter((k) => k !== record.id)
              : [...prev, record.id]
          );
        },
        style: { cursor: "pointer" },
      })}
    />
  );

  const paneCard = (accent, icon, title, count, actions, children) => (
    <div
      style={{
        border: `1px solid ${accent.border}`,
        borderRadius: 8,
        overflow: "hidden",
        height: "100%",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          background: accent.bg,
          borderBottom: `1px solid ${accent.border}`,
          padding: "8px 12px",
        }}
      >
        <strong style={{ color: accent.text }}>
          {icon} {title} ({count})
        </strong>
        {actions}
      </div>
      <div style={{ padding: 12 }}>{children}</div>
    </div>
  );

  // Arrow directions follow the visual pane order, which mirrors in RTL
  const assignIcon = isRTL ? <ArrowLeftOutlined /> : <ArrowRightOutlined />;
  const removeIcon = isRTL ? <ArrowRightOutlined /> : <ArrowLeftOutlined />;

  return (
    <Drawer
      title={
        fixedUser
          ? `${trans("Assign POIs")}${userName ? ` — ${userName}` : ""}`
          : trans("Bulk Assign POIs")
      }
      open={open}
      onClose={handleCancel}
      width="min(1150px, 95vw)"
      // Must sit above the react-bootstrap connections Modal (z-index 1055)
      zIndex={1200}
      destroyOnClose
      footer={
        <div className="d-flex align-items-center justify-content-between">
          <Space>
            {toAdd.size > 0 && (
              <Tag color="green">
                {fixedUser
                  ? `+${toAdd.size} ${trans("added")}`
                  : `+${toAdd.size} ${trans("POIs")} → ${staffCount} ${trans("staff")}`}
              </Tag>
            )}
            {toRemove.size > 0 && (
              <Tag color="red">
                {fixedUser
                  ? `-${toRemove.size} ${trans("removed")}`
                  : `-${toRemove.size} ${trans("POIs")} (${staffCount} ${trans("staff")})`}
              </Tag>
            )}
            {!hasChanges && (
              <span style={{ color: "#999" }}>{trans("No pending changes")}</span>
            )}
          </Space>
          <Space>
            <Button onClick={handleCancel}>{trans("Cancel")}</Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              {trans("Save")}
            </Button>
          </Space>
        </div>
      }
    >
      {/* ---------- Staff selector (bulk mode only) ---------- */}
      {!fixedUser && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <strong style={{ whiteSpace: "nowrap" }}>{trans("Field Staff")}:</strong>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={trans("Select one or more field staff")}
            style={{ flex: 1 }}
            maxTagCount="responsive"
            options={staffOptions}
            value={selectedStaffIds}
            onChange={handleStaffChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
          <Tag>{`${staffCount} ${trans("selected")}`}</Tag>
        </div>
      )}

      <div className="d-flex flex-column flex-lg-row gap-3">
        {/* ---------- Available POIs ---------- */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {paneCard(
            AVAILABLE_ACCENT,
            <UnorderedListOutlined />,
            trans("Available POIs"),
            availableRows.length,
            <Button
              size="small"
              type="primary"
              ghost
              disabled={availableRows.length === 0 || staffCount === 0}
              onClick={() => assignIds(availableRows.map((p) => p.id))}
            >
              {trans("Assign all filtered")}
            </Button>,
            <>
              <Space className="mb-2" style={{ width: "100%" }} wrap>
                <Input.Search
                  allowClear
                  placeholder={trans("Search POIs")}
                  style={{ width: 180 }}
                  onChange={(e) => setLeftSearch(e.target.value)}
                  value={leftSearch}
                />
                <Select
                  allowClear
                  showSearch
                  placeholder={trans("Type")}
                  style={{ minWidth: 120 }}
                  options={typeOptions}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  // Render inside the drawer so the dropdown isn't buried
                  // beneath the drawer's raised z-index (1200)
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
                <Select
                  allowClear
                  showSearch
                  placeholder={trans("City")}
                  style={{ minWidth: 120 }}
                  options={cityOptions}
                  value={cityFilter}
                  onChange={setCityFilter}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Space>
              {paneTable(
                availableRows,
                leftColumns,
                leftSelected,
                setLeftSelected,
                loadingPois,
                staffCount === 0
                  ? trans("Select field staff first")
                  : trans("No POIs match your search or filters")
              )}
            </>
          )}
        </div>

        {/* ---------- Transfer arrows ---------- */}
        <div className="d-flex flex-row flex-lg-column justify-content-center align-items-center gap-2">
          <Tooltip
            title={`${trans("Assign selected")}${
              leftSelected.length > 0 ? ` (${leftSelected.length})` : ""
            }`}
            placement={isRTL ? "left" : "right"}
          >
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={assignIcon}
              disabled={leftSelected.length === 0 || staffCount === 0}
              onClick={() => assignIds(leftSelected)}
            />
          </Tooltip>
          <Tooltip
            title={`${trans("Remove selected")}${
              rightSelected.length > 0 ? ` (${rightSelected.length})` : ""
            }`}
            placement={isRTL ? "left" : "right"}
          >
            <Button
              danger
              shape="circle"
              size="large"
              icon={removeIcon}
              disabled={rightSelected.length === 0}
              onClick={() => removeIds(rightSelected)}
            />
          </Tooltip>
        </div>

        {/* ---------- Assigned POIs ---------- */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {paneCard(
            ASSIGNED_ACCENT,
            <CheckCircleOutlined />,
            trans("Assigned"),
            assignedRows.length,
            <Button
              size="small"
              danger
              ghost
              disabled={assignedRows.length === 0}
              onClick={() => removeIds(assignedRows.map((p) => p.id))}
            >
              {trans("Remove all filtered")}
            </Button>,
            <>
              <Space className="mb-2" style={{ width: "100%" }} wrap>
                <Input.Search
                  allowClear
                  placeholder={trans("Search assigned POIs")}
                  style={{ width: 180 }}
                  onChange={(e) => setRightSearch(e.target.value)}
                  value={rightSearch}
                />
              </Space>
              {paneTable(
                assignedRows,
                rightColumns,
                rightSelected,
                setRightSelected,
                loadingAssignments,
                staffCount === 0
                  ? trans("Select field staff first")
                  : fixedUser
                    ? trans("No POIs assigned yet — select POIs from the left to assign")
                    : trans("No POIs assigned to all selected staff yet")
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}
